import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import EntityFormModal from '@sharedCR/common/EntityFormModal';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { PENDING_CHANGES_SUBTAB } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/PendingChangesDrawer/PendingChangesDrawer.constants';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import { filterDisplayableEnabledDefinitionsForTarget } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.utils';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { MAX_BORROWERS_COUNT } from 'constants/loanApplication';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useReleaseMyFieldLocks from 'hooks/useReleaseMyFieldLocks';
import useRevertFieldValues from 'hooks/useRevertFieldValues';
import { getConditionalArray } from 'utils/data';
import { calculatePendingChanges } from 'utils/pendingChanges';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { loadCustomFieldsAction } from 'store/customFieldDefinitions/actions';
import { clearPendingBorrowerContactLink } from 'store/loanApplications/actions';
import {
  selectHiddenFieldIds,
  selectHiddenPageIds,
  selectIncompleteLinkedNavByBorrower,
  selectIncompleteLinkedNavLoanScoped,
} from 'store/loanApplications/selectors';
import { clearLocalEditedFieldsAction } from 'store/presence/actions';
import { selectLocalEditedFieldPaths } from 'store/presence/selectors';
import { selectCustomFields } from 'store/system/selectors/customFieldDefinitions.selectors';

import Content from './Content';
import {
  EDIT_ENTITY_DEFAULT_SECTION,
  EDIT_ENTITY_TABS_HREF_CONFIG,
  EDIT_ENTITY_TITLE_CONFIG,
  ENTITY_TYPE_HREF,
  getHeadTabs,
  getNavigationTabs,
  NEW_ENTITY,
  NEW_ENTITY_INITIAL_DATA,
  NEW_ENTITY_TITLE_CONFIG,
} from './EditEntityModal.constants';
import { checkIsNbeLandingRoute } from './EditEntityModal.utils';

const defaultArray = [];

const EditEntityModal = ({ entitiesData = defaultArray, isLoading }) => {
  const dispatch = useDispatch();

  const { applicationId, entityId, section, subtab, tab } = useParams();
  const history = useHistory();
  const revertFieldValues = useRevertFieldValues();
  const releaseMyFieldLocks = useReleaseMyFieldLocks();

  const localEditedFieldPaths = useSelector(selectLocalEditedFieldPaths);
  const hiddenPageIds = useSelector(selectHiddenPageIds);
  const hiddenFieldIds = useSelector(selectHiddenFieldIds);
  const masterCompanyId = useSelector(selectMasterCompanyId);
  const allCustomFieldDefinitions = useSelector(selectCustomFields);
  const navNeededByBorrower = useSelector(selectIncompleteLinkedNavByBorrower());
  const navNeededLoanScoped = useSelector(selectIncompleteLinkedNavLoanScoped());

  const borrowerCustomFieldDefinitions = useMemo(
    () =>
      filterDisplayableEnabledDefinitionsForTarget(
        allCustomFieldDefinitions,
        CUSTOM_FIELDS_TARGET.BORROWER,
      ),
    [allCustomFieldDefinitions],
  );

  const nbeCustomFieldDefinitions = useMemo(
    () =>
      filterDisplayableEnabledDefinitionsForTarget(
        allCustomFieldDefinitions,
        CUSTOM_FIELDS_TARGET.NBE,
      ),
    [allCustomFieldDefinitions],
  );

  const shouldShowBorrowerCustomFields = !!borrowerCustomFieldDefinitions.length;
  const shouldShowNbeCustomFields = !!nbeCustomFieldDefinitions.length;
  const isPendingChangesDrawerOpen = subtab === PENDING_CHANGES_SUBTAB;

  useEffectSafeStrictMode(() => {
    if (!masterCompanyId) {
      return;
    }

    dispatch(
      loadCustomFieldsAction({
        companyId: masterCompanyId,
        targets: [CUSTOM_FIELDS_TARGET.BORROWER, CUSTOM_FIELDS_TARGET.NBE],
        enabledOnly: true,
        merge: true,
      }),
    );
  }, [dispatch, masterCompanyId]);

  const isCorrectPath =
    section &&
    entityId &&
    Object.values(ENTITY_TYPE_HREF).includes(subtab) &&
    (entityId !== NEW_ENTITY || entitiesData.length < MAX_BORROWERS_COUNT);
  const isNewEntity = entityId === NEW_ENTITY;
  const title = isNewEntity ? NEW_ENTITY_TITLE_CONFIG[subtab] : EDIT_ENTITY_TITLE_CONFIG[subtab];

  const [isNewEntityFlow, setIsNewEntityFlow] = useState(isNewEntity);
  const [isNestedEntityEditActive, setIsNestedEntityEditActive] = useState(false);

  useEffect(() => {
    if (!isNewEntity) {
      return;
    }

    setIsNewEntityFlow(true);
  }, [isNewEntity]);

  const data = useMemo(() => {
    const isBorrowerFlow = subtab === ENTITY_TYPE_HREF.BORROWER;

    return isNewEntityFlow
      ? [...NEW_ENTITY_INITIAL_DATA[subtab], ...getConditionalArray(isBorrowerFlow, entitiesData)]
      : entitiesData;
  }, [entitiesData, isNewEntityFlow, subtab]);

  const onClose = useCallback(() => {
    if (localEditedFieldPaths.length) {
      releaseMyFieldLocks();
      revertFieldValues(localEditedFieldPaths);
      dispatch(clearLocalEditedFieldsAction());
    }

    // Discard any pending borrower -> contact link on close. After a successful
    // save it was already persisted + cleared, so this is a no-op then; on
    // cancel it drops the pending link so it is not kept around.
    if (subtab === ENTITY_TYPE_HREF.BORROWER) {
      dispatch(clearPendingBorrowerContactLink({ borrowerId: entityId }));
    }

    setIsNewEntityFlow(false);
    setPendingChanges({});
    setPendingChangesCounts({});

    history.push(
      LoansPaths.applicationManagement.generatePath({ applicationId, tab: 'borrowers' }),
    );
  }, [
    history,
    applicationId,
    localEditedFieldPaths,
    revertFieldValues,
    releaseMyFieldLocks,
    dispatch,
    subtab,
    entityId,
  ]);

  const { deletedEntities, newEntities } = useMemo(
    () =>
      entitiesData.reduce(
        (acc, cur) => {
          if (cur.pendingChangesActionType === PendingChangesActionType.DELETED) {
            acc.deletedEntities.push(cur.id);
          }

          if (cur.pendingChangesActionType === PendingChangesActionType.ADDED) {
            acc.newEntities.push(cur.id);
          }

          return acc;
        },
        { deletedEntities: [], newEntities: getConditionalArray(isNewEntityFlow, [NEW_ENTITY]) },
      ),
    [entitiesData, isNewEntityFlow],
  );

  const [pendingChanges, setPendingChanges] = useState({});
  const [pendingChangesCounts, setPendingChangesCounts] = useState({});
  const [invalidTabs, setInvalidTabs] = useState([]);
  const [invalidHeadTabs, setInvalidHeadTabs] = useState([]);

  const onCountPendingChanges = useCallback(pendingChanges => {
    setPendingChangesCounts(calculatePendingChanges(pendingChanges, true));
  }, []);

  const additionalProps = useMemo(
    () => ({
      newEntities,
      deletedEntities,
      isNewEntityFlow,
      onCountPendingChanges,
      setPendingChanges,
      setInvalidTabs,
      setInvalidHeadTabs,
      data,
    }),
    [deletedEntities, isNewEntityFlow, onCountPendingChanges, setPendingChanges, newEntities, data],
  );

  const isNbeLandingRoute = checkIsNbeLandingRoute({ subtab, entityId });

  useEffect(() => {
    if (isCorrectPath || isPendingChangesDrawerOpen || isNbeLandingRoute) {
      return;
    }

    onClose();
    setIsNestedEntityEditActive(false);
  }, [isCorrectPath, onClose, isPendingChangesDrawerOpen, isNbeLandingRoute]);

  const headTabs = useMemo(
    () =>
      getHeadTabs({
        applicationId,
        tab,
        subtab,
        section,
        data,
        history,
        deletedEntities,
        newEntities,
        pendingChangesCounts,
        pendingChanges,
        isNestedEntityEditActive,
        setIsNestedEntityEditActive,
        dispatch,
        invalidHeadTabs,
        navNeededByBorrower,
        navNeededLoanScoped,
      }),
    [
      applicationId,
      tab,
      subtab,
      section,
      data,
      history,
      deletedEntities,
      newEntities,
      pendingChangesCounts,
      pendingChanges,
      isNestedEntityEditActive,
      dispatch,
      invalidHeadTabs,
      navNeededByBorrower,
      navNeededLoanScoped,
    ],
  );

  const navigationTabs = useMemo(
    () =>
      getNavigationTabs({
        applicationId,
        tab,
        subtab,
        section,
        entityId,
        data,
        history,
        deletedEntities,
        pendingChanges,
        newEntities,
        isNestedEntityEditActive,
        setIsNestedEntityEditActive,
        dispatch,
        invalidTabs,
        hiddenPageIds,
        hiddenFieldIds,
        shouldShowBorrowerCustomFields,
        shouldShowNbeCustomFields,
        navNeededByBorrower,
        navNeededLoanScoped,
      }),
    [
      applicationId,
      tab,
      subtab,
      section,
      entityId,
      data,
      history,
      deletedEntities,
      pendingChanges,
      newEntities,
      isNestedEntityEditActive,
      dispatch,
      invalidTabs,
      hiddenPageIds,
      hiddenFieldIds,
      shouldShowBorrowerCustomFields,
      shouldShowNbeCustomFields,
      navNeededByBorrower,
      navNeededLoanScoped,
    ],
  );

  useEffect(() => {
    if (!isCorrectPath || isPendingChangesDrawerOpen) {
      return;
    }

    const tabHrefs = EDIT_ENTITY_TABS_HREF_CONFIG[subtab];

    if (Object.values(tabHrefs).includes(section)) {
      return;
    }

    history.push(
      LoansPaths.applicationManagement.generatePath({
        applicationId,
        entityId,
        section: EDIT_ENTITY_DEFAULT_SECTION[subtab],
        tab,
        subtab,
      }),
    );
  }, [
    applicationId,
    entityId,
    section,
    tab,
    subtab,
    history,
    isCorrectPath,
    isPendingChangesDrawerOpen,
  ]);

  return (
    <EntityFormModal
      Content={Content}
      additionalProps={additionalProps}
      headTabs={headTabs}
      headValue={entityId}
      isData={!!data.length}
      isLoading={isLoading}
      isOpen={!!isCorrectPath}
      navigationTabs={navigationTabs}
      navigationValue={section}
      setIsNestedEntityEditActive={setIsNestedEntityEditActive}
      title={title}
      onClose={onClose}
    />
  );
};

EditEntityModal.propTypes = {
  entitiesData: PropTypes.array,
  ...WithLoadingPropTypes,
};

export default memo(EditEntityModal);
